/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'components/core/Tooltip';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';

import _get from 'lodash/get';

import { countryShape } from 'constants/PropTypesShapes';

import { getPhoneCode } from 'libphonenumber-js';
import _toLower from 'lodash/toLower';

import { ReactComponent as X } from '../icons/X.svg';
import { ReactComponent as MagnifyingGlassIcon } from '../icons/MagnifyingGlass.svg';
import { ReactComponent as ArrowIcon } from '../icons/Arrow.svg';
import { ReactComponent as Information } from '../icons/Information.svg';

import commonStyles from '../common.cssmodule.css';
import styles from './style.cssmodule.css';

const DEFAULT_COUNTRY_CODE = 'FR';

const flagContext = require.context('assets/images/flags/4x3', false, /\.svg$/);

const Flag = ({ countryCode }) => {
  try {
    const flagModule = flagContext(`./${_toLower(countryCode)}.svg`);
    const flagPath = flagModule.default;
    return <div className={styles.flag} style={{ backgroundImage: `url('${flagPath}')` }} />;
  } catch (error) {
    return null;
  }
};

Flag.propTypes = {
  countryCode: PropTypes.string,
};

Flag.defaultProps = {
  countryCode: DEFAULT_COUNTRY_CODE,
};

const InputPhone = ({
  label,
  hint,
  error,
  fullWidth,
  dotColor,
  tooltipId,

  countryCode,
  defaultCountryCode,
  changeCountryCode,
  countries,

  dispatch,

  ...inputProps
}) => {
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const [countryFilter, setCountryFilter] = useState('');

  useEffect(() => {
    if (!countryCode && defaultCountryCode) {
      changeCountryCode(defaultCountryCode);
    }
  }, [countryCode, defaultCountryCode]);

  const inputRef = useRef(null);
  const selectCountryRef = useRef(null);
  const filterCountryRef = useRef(null);

  const filteredCountries = countries.filter(({ label: countryLabel, code }) => {
    const searchIsEmpty = !countryFilter;
    const matchSearch = countryLabel.toLowerCase().trim().includes(countryFilter.toLowerCase().trim());
    const notCurrentCountryCode = code !== countryCode;
    return (searchIsEmpty || matchSearch) && notCurrentCountryCode;
  });

  const handleCountryFilter = (e) => {
    const { value } = e.target;
    setCountryFilter(value);
  };

  const handleSelectCountry = (newCountryCode) => {
    changeCountryCode(newCountryCode);
    setIsOpen(false);
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    const handleClickOutsideCountrySelector = (event) => {
      if (selectCountryRef.current && !selectCountryRef.current.contains(event.target)) {
        setIsOpen(false);
        setCountryFilter('');
      }
    };
    document.addEventListener('click', handleClickOutsideCountrySelector);
    return () => document.removeEventListener('click', handleClickOutsideCountrySelector);
  }, []);

  const prefix = countryCode ? getPhoneCode(countryCode) : '';

  const CountryFilter = (
    <div className={styles.filter}>
      <MagnifyingGlassIcon />
      <input ref={filterCountryRef} tabIndex={-1} type="text" name="countryFilter" id="countryFilter" onChange={handleCountryFilter} value={countryFilter} />
    </div>
  );

  const CountryOption = ({ code, label: countryLabel }) => (
    <div
      title={countryLabel}
      key={code}
      className={styles.option}
      onClick={() => handleSelectCountry(code)}
    >
      <Flag countryCode={code} />
      <span className={styles.label}>{countryLabel}</span>
    </div>
  );
  CountryOption.propTypes = {
    code: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  };

  const NoCountryAvailable = <p className={styles.noCountryAvailable}>{intl.formatMessage({ id: 'info.noCountryAvailable' })}</p>;

  const CountrySelector = (
    <div
      ref={selectCountryRef}
      className={`${styles.countrySelector} ${isOpen ? `${styles.open} expand-container` : ''}`}
    >
      <div
        className={styles.selectTrigger}
        role="button"
        onClick={() => {
          setTimeout(() => filterCountryRef.current.focus(), 200);
          setIsOpen(!isOpen);
        }}
      >
        <Flag countryCode={countryCode} />
        <span>{countryCode}</span>
        {!inputProps.disabled && <ArrowIcon className={styles.arrow} />}
      </div>
      <div className={styles.options}>
        {CountryFilter}
        {filteredCountries.length > 0 ? filteredCountries.map(CountryOption) : NoCountryAvailable}
      </div>
    </div>
  );

  /* ============= */
  /* ||||||||||||| */
  /* ============= */

  const { name, required } = inputProps;

  const DotColor = dotColor && <div className={commonStyles.dotColor} style={{ backgroundColor: dotColor }} />;
  const Label = <label htmlFor={name} className={commonStyles.label} >{`${label}${required ? ' *' : ''}`}</label>;

  const InformationTooltip = tooltipId && (
    <Tooltip content={(<LocalizedMessage id={tooltipId} />)}>
      <Information className={commonStyles.infoIcon} />
    </Tooltip>
  );

  const Hint = (!error && hint)
    ? (
      <span className={commonStyles.hint}>
        {hint}
      </span>
    ) : null;

  const ErrorMessage = (
    <span className={commonStyles.error}>
      {error ? (
        <React.Fragment>
          <X />
          <span>{error}</span>
        </React.Fragment>
      ) : null}
    </span>
  );
  const Prefix = !inputProps.disabled ? <span className={styles.prefix}>{`+${prefix}`}</span> : null;
  const Divider = !inputProps.disabled ? <div className={styles.dividerVertical} /> : null;
  const Tel = <input {...inputProps} value={inputProps.disabled ? `+${prefix}${inputProps.value}` : inputProps.value} maxLength={15} type="tel" ref={inputRef} />;

  return (
    <div className={commonStyles.field} style={{ gridColumn: fullWidth ? '1 / -1' : 'span 1' }}>
      {label && (
      <div className={commonStyles.fieldHeader}>
        {DotColor}
        {Label}
        {InformationTooltip}
      </div>
      )}
      <div className={commonStyles.inputContainer}>
        {CountrySelector}
        {Divider}
        {Prefix}
        {Tel}
      </div>
      {Hint}
      {ErrorMessage}
    </div>
  );
};

const mapStateToProps = (state) => ({
  countries: _get(state, 'country.all', []),
  defaultCountryCode: _get(state, 'intl.countryCode'),
});

InputPhone.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  error: PropTypes.string,
  hint: PropTypes.string,
  fullWidth: PropTypes.bool,
  dotColor: PropTypes.string,
  tooltipId: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  dispatch: PropTypes.func,

  countryCode: PropTypes.string,
  changeCountryCode: PropTypes.func,
  countries: PropTypes.arrayOf(countryShape),
  defaultCountryCode: PropTypes.string,
};
InputPhone.defaultProps = {
  label: null,
  fullWidth: false,
  error: null,
  tooltipId: null,
  hint: null,
  dotColor: null,
  value: '',
  required: false,
  onChange: () => {},
  dispatch: () => {},

  countryCode: null,
  changeCountryCode: () => {},
  countries: [],
  defaultCountryCode: DEFAULT_COUNTRY_CODE,
};

module.exports = connect(mapStateToProps)(InputPhone);



// WEBPACK FOOTER //
// ./components/inputs/InputPhone/InputPhone.jsx